import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Card, CardBody, Table as ReactstrapTable } from 'reactstrap';

import EnumStatus from '../../../common/EnumStatus';

import {
  PREVENTIVE_TASK_EXECUTION_STATUS,
  PREVENTIVE_TASK_STATUS_ACTIVITY,
} from '../../../../config/constants';

import { formatDateTitle } from './utils/PreventiveTaskProjectionCalendarUtils';
import PreventiveTaskProjectionExecutionModal from './execution/PreventiveTaskProjectionExecutionModal';
import { getPreventiveTaskResource } from '../../../../actions/task-schedule/PreventiveTask';

export const formatTimestampDateTitle = (timestamp) => {
  if (!timestamp) return '-';

  const date = new Date(timestamp);
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toUpperCase();

const normalizeRowType = (rowType) => normalizeText(rowType);

const getProjectionGroupKey = (item) =>
  item.projectionId || `${item.preventiveTaskId}-${item.projectedDate || ''}`;

const groupResourceRowsByProjection = (items) => {
  const grouped = {};

  items.forEach((item) => {
    const rowType = normalizeRowType(item.rowType);
    const projectionKey = getProjectionGroupKey(item);
    const key = `PROJECTION-${projectionKey}`;

    if (rowType === 'RESOURCE') {
      if (!grouped[key]) {
        grouped[key] = {
          ...item,
          groupKey: key,
          rowType: 'RESOURCE_GROUP',
          projectionResourceId: null,
          resourceId: null,
          resourceName: null,
          resources: [],
        };
      }

      if (grouped[key].rowType === 'TASK') {
        grouped[key] = {
          ...grouped[key],
          rowType: 'RESOURCE_GROUP',
          projectionResourceId: null,
          resourceId: null,
          resourceName: null,
        };
      }

      grouped[key].resources.push(item);
      return;
    }

    const existingResources =
      grouped[key] && grouped[key].resources ? grouped[key].resources : [];

    grouped[key] = {
      ...item,
      groupKey: key,
      rowType: existingResources.length ? 'RESOURCE_GROUP' : 'TASK',
      projectionResourceId: existingResources.length
        ? null
        : item.projectionResourceId,
      resourceId: existingResources.length ? null : item.resourceId,
      resourceName: existingResources.length ? null : item.resourceName,
      resources: existingResources,
    };
  });

  return Object.keys(grouped).map((key) => grouped[key]);
};

const projectionItemPropType = PropTypes.shape({
  rowType: PropTypes.string,
  projectionId: PropTypes.number,
  projectionResourceId: PropTypes.number,
  preventiveTaskId: PropTypes.number,
  preventiveTaskName: PropTypes.string,
  companyPreventiveTaskId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  baseDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resourceId: PropTypes.number,
  resourceName: PropTypes.string,
  alertStatus: PropTypes.string,
  resources: PropTypes.arrayOf(PropTypes.shape({})),
});

const PreventiveTaskProjectionDayDetail = ({
  selectedDateKey,
  selectedDayItems,
  onClickExecute,
  loading,
  dispatchGetPreventiveTaskResource,
  title,
  emptyMessage,
  showProjectedDate,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemResources, setSelectedItemResources] = useState([]);

  const groupedDayItems = useMemo(
    () => groupResourceRowsByProjection(selectedDayItems),
    [selectedDayItems],
  );

  const closeModal = () => {
    setSelectedItem(null);
    setSelectedItemResources([]);
  };

  const openExecutionModal = async (item) => {
    setSelectedItem(item);
    setSelectedItemResources([]);

    if (item.rowType === 'TASK') {
      const resources = await dispatchGetPreventiveTaskResource({
        preventiveTaskId: item.preventiveTaskId,
      });

      setSelectedItemResources(resources || []);
      return;
    }

    if (
      item.rowType === 'RESOURCE_GROUP' &&
      item.resources &&
      item.resources.length
    ) {
      setSelectedItemResources(item.resources);
      return;
    }

    if (item.rowType === 'RESOURCE') {
      setSelectedItemResources([item]);
    }
  };

  const onSubmitExecution = async (formValues) => {
    await onClickExecute(selectedItem, formValues, selectedItemResources);
    closeModal();
  };

  return (
    <Card>
      <CardBody>
        <h4>
          {title || `Detalle del día: ${formatDateTitle(selectedDateKey)}`}
        </h4>

        {!groupedDayItems.length ? (
          <p className="text-muted mb-0">
            {emptyMessage || 'No hay proyecciones para este día.'}
          </p>
        ) : (
          <ReactstrapTable bordered responsive size="sm">
            <thead>
              <tr>
                <th className="text-center">Estado</th>
                <th>Tarea</th>
                <th>Recurso</th>

                {showProjectedDate && <th>Fecha programada</th>}

                <th>Última ejecución</th>
                <th>Acción</th>
              </tr>
            </thead>

            <tbody>
              {groupedDayItems.map((item) => {
                const resourceCount =
                  item.resources && item.resources.length
                    ? item.resources.length
                    : 0;

                return (
                  <tr key={item.groupKey}>
                    <td className="text-center align-middle">
                      <EnumStatus
                        enumObject={PREVENTIVE_TASK_STATUS_ACTIVITY}
                        enumValue={item.alertStatus}
                      />
                    </td>

                    <td>
                      <div>
                        <strong>{item.preventiveTaskName}</strong>
                      </div>

                      <small className="text-muted">
                        Código: {item.companyPreventiveTaskId || '-'}
                      </small>
                    </td>

                    <td>
                      {item.rowType === 'RESOURCE_GROUP'
                        ? `${resourceCount} recursos`
                        : item.resourceName || '-'}
                    </td>

                    {showProjectedDate && (
                      <td className="align-middle">
                        {formatTimestampDateTitle(item.projectedDate)}
                      </td>
                    )}

                    <td className="align-middle">
                      {formatTimestampDateTitle(item.baseDate)}
                    </td>

                    <td className="align-middle">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => openExecutionModal(item)}
                      >
                        Ejecutar
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ReactstrapTable>
        )}

        <PreventiveTaskProjectionExecutionModal
          show={!!selectedItem}
          onClose={closeModal}
          projectionItem={selectedItem}
          taskResources={selectedItemResources}
          loading={loading}
          initialValues={{
            status: PREVENTIVE_TASK_EXECUTION_STATUS.DONE,
            startedDate: null,
            completedDate: null,
            comment: null,
            completedResourceIds: [],
          }}
          onSubmit={onSubmitExecution}
        />
      </CardBody>
    </Card>
  );
};

PreventiveTaskProjectionDayDetail.propTypes = {
  selectedDateKey: PropTypes.string,
  selectedDayItems: PropTypes.arrayOf(projectionItemPropType),
  onClickExecute: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  dispatchGetPreventiveTaskResource: PropTypes.func.isRequired,
  title: PropTypes.string,
  emptyMessage: PropTypes.string,
  showProjectedDate: PropTypes.bool,
};

PreventiveTaskProjectionDayDetail.defaultProps = {
  selectedDateKey: null,
  selectedDayItems: [],
  loading: false,
  title: null,
  emptyMessage: null,
  showProjectedDate: false,
};

const mapDispatchToProps = {
  dispatchGetPreventiveTaskResource: getPreventiveTaskResource,
};

export default connect(
  null,
  mapDispatchToProps,
)(PreventiveTaskProjectionDayDetail);
